export interface LoanInputs {
  landPrice: number
  downPayment: number
  downPaymentType: "dollar" | "percent"
  interestRate: number
  loanTermYears: number
  balloonEnabled: boolean
  balloonYear: number
  extraMonthlyPayment: number
}

export interface AmortizationRow {
  month: number
  payment: number
  principal: number
  interest: number
  extraPayment: number
  totalPayment: number
  balance: number
}

export interface LoanResults {
  landPrice: number
  interestRate: number
  loanTermYears: number
  monthlyPayment: number
  totalInterest: number
  totalCost: number
  payoffDate: string
  loanAmount: number
  downPaymentAmount: number
  balloonPaymentAmount: number
  schedule: AmortizationRow[]
  actualMonths: number
}

export function calculateLoan(inputs: LoanInputs): LoanResults {
  const downPaymentAmount =
    inputs.downPaymentType === "percent"
      ? (inputs.landPrice * inputs.downPayment) / 100
      : inputs.downPayment

  const loanAmount = inputs.landPrice - downPaymentAmount
  if (loanAmount <= 0) {
    return {
      landPrice: inputs.landPrice,
      interestRate: inputs.interestRate,
      loanTermYears: inputs.loanTermYears,
      monthlyPayment: 0,
      totalInterest: 0,
      totalCost: inputs.landPrice,
      payoffDate: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long" }),
      loanAmount: 0,
      downPaymentAmount,
      balloonPaymentAmount: 0,
      schedule: [],
      actualMonths: 0,
    }
  }

  const monthlyRate = inputs.interestRate / 100 / 12
  const totalMonths = inputs.loanTermYears * 12

  let monthlyPayment: number
  if (monthlyRate === 0) {
    monthlyPayment = loanAmount / totalMonths
  } else {
    monthlyPayment =
      (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) /
      (Math.pow(1 + monthlyRate, totalMonths) - 1)
  }

  const schedule: AmortizationRow[] = []
  let balance = loanAmount
  let totalInterest = 0
  let totalPaid = 0
  let balloonPaymentAmount = 0
  const balloonMonth = inputs.balloonEnabled ? inputs.balloonYear * 12 : totalMonths

  for (let month = 1; month <= totalMonths; month++) {
    if (balance <= 0) break

    const interestPayment = balance * monthlyRate
    let principalPayment = monthlyPayment - interestPayment
    let extra = Math.min(inputs.extraMonthlyPayment, balance - principalPayment)
    if (extra < 0) extra = 0

    // Check for balloon payment
    if (inputs.balloonEnabled && month === balloonMonth) {
      balloonPaymentAmount = balance
      const totalPaymentThisMonth = balance + interestPayment
      schedule.push({
        month,
        payment: monthlyPayment,
        principal: balance,
        interest: interestPayment,
        extraPayment: 0,
        totalPayment: totalPaymentThisMonth,
        balance: 0,
      })
      totalInterest += interestPayment
      totalPaid += totalPaymentThisMonth
      balance = 0
      break
    }

    // Ensure we don't overpay
    if (principalPayment + extra > balance) {
      principalPayment = balance
      extra = 0
    }

    balance -= principalPayment + extra
    if (balance < 0.01) balance = 0

    totalInterest += interestPayment
    const totalPaymentThisMonth = principalPayment + interestPayment + extra
    totalPaid += totalPaymentThisMonth

    schedule.push({
      month,
      payment: monthlyPayment,
      principal: principalPayment,
      interest: interestPayment,
      extraPayment: extra,
      totalPayment: totalPaymentThisMonth,
      balance: Math.max(0, balance),
    })

    if (balance <= 0) break
  }

  const actualMonths = schedule.length
  const payoffDate = new Date()
  payoffDate.setMonth(payoffDate.getMonth() + actualMonths)
  const payoffDateStr = payoffDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  })

  return {
    landPrice: inputs.landPrice,
    interestRate: inputs.interestRate,
    loanTermYears: inputs.loanTermYears,
    monthlyPayment,
    totalInterest,
    totalCost: downPaymentAmount + totalPaid,
    payoffDate: payoffDateStr,
    loanAmount,
    downPaymentAmount,
    balloonPaymentAmount,
    schedule,
    actualMonths,
  }
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}
